import { Howl } from 'howler';

/**
 * SoundManager - Singleton class for managing all game audio
 * Handles sound effects, background music, volume control, and muting
 */
class SoundManager {
  constructor() {
    if (SoundManager.instance) {
      return SoundManager.instance;
    }

    this.sounds = {};
    this.backgroundMusic = null;
    this.initialized = false;
    this.unlocked = false; // Track if audio context is unlocked
    this.masterVolume = 0.5;
    this.sfxVolume = 0.5;
    this.musicVolume = 0.5;
    this.muted = false;

    SoundManager.instance = this;
  }

  /**
   * Initialize all sounds - call this once when app starts
   */
  init() {
    if (this.initialized) {
      return;
    }

    try {
      // Game sounds
      this.sounds.roundStart = new Howl({
        src: ['/sounds/game/round-start.wav'],
        volume: this.masterVolume * this.sfxVolume,
        preload: true,
      });

      this.sounds.roundEnd = new Howl({
        src: ['/sounds/game/round-end.mp3'],
        volume: this.masterVolume * this.sfxVolume,
        preload: true,
      });

      this.sounds.countdown = new Howl({
        src: ['/sounds/game/countdown.mp3'],
        volume: this.masterVolume * this.sfxVolume,
        preload: true,
      });

      // Background music (looping)
      this.backgroundMusic = new Howl({
        src: ['/sounds/game/background-loop.mp3'],
        volume: this.masterVolume * this.musicVolume,
        loop: true,
        preload: true,
      });

      // Action sounds
      this.sounds.betPlace = new Howl({
        src: ['/sounds/actions/bet-place.wav'],
        volume: this.masterVolume * this.sfxVolume,
        preload: true,
      });

      this.sounds.cashout = new Howl({
        src: ['/sounds/actions/cashout.wav'],
        volume: this.masterVolume * this.sfxVolume,
        preload: true,
      });

      this.sounds.betCancel = new Howl({
        src: ['/sounds/actions/bet-cancel.wav'],
        volume: this.masterVolume * this.sfxVolume,
        preload: true,
      });

      this.sounds.betNextRound = new Howl({
        src: ['/sounds/actions/bet-next-round.wav'],
        volume: this.masterVolume * this.sfxVolume,
        preload: true,
      });

      // UI sounds
      this.sounds.buttonClick = new Howl({
        src: ['/sounds/ui/button-click.wav'],
        volume: this.masterVolume * this.sfxVolume,
        preload: true,
      });

      this.sounds.toggleSwitch = new Howl({
        src: ['/sounds/ui/toggle-switch.wav'],
        volume: this.masterVolume * this.sfxVolume,
        preload: true,
      });

      this.sounds.increment = new Howl({
        src: ['/sounds/ui/increment.wav'],
        volume: this.masterVolume * this.sfxVolume,
        preload: true,
      });

      this.sounds.decrement = new Howl({
        src: ['/sounds/ui/decrement.wav'],
        volume: this.masterVolume * this.sfxVolume,
        preload: true,
      });

      this.sounds.quickBet = new Howl({
        src: ['/sounds/ui/quick-bet.wav'],
        volume: this.masterVolume * this.sfxVolume,
        preload: true,
      });

      this.initialized = true;
    } catch (error) {
      console.error('Error initializing SoundManager:', error);
      // Silent fallback - don't break the app if sounds fail
    }
  }

  /**
   * Unlock audio context (required for browser autoplay policy)
   * Call this on first user interaction
   */
  unlockAudio() {
    if (this.unlocked) {
      return; // Already unlocked
    }

    try {
      // Play a silent sound to unlock the audio context
      const unlockSound = new Howl({
        src: ['/sounds/ui/button-click.wav'],
        volume: 0,
        onend: function() {
          this.unload();
        }
      });
      unlockSound.play();
      this.unlocked = true;
    } catch (error) {
      console.error('Error unlocking audio:', error);
    }
  }

  /**
   * Play a sound effect by name
   * @param {string} soundName - Name of the sound to play
   */
  playSoundEffect(soundName) {
    if (!this.initialized || this.muted || !this.unlocked) {
      return; // Don't queue sounds before audio is unlocked
    }

    try {
      // Map friendly names to sound objects
      const soundMap = {
        'round-start': 'roundStart',
        'round-end': 'roundEnd',
        'countdown': 'countdown',
        'bet-place': 'betPlace',
        'cashout': 'cashout',
        'bet-cancel': 'betCancel',
        'bet-next-round': 'betNextRound',
        'button-click': 'buttonClick',
        'toggle-switch': 'toggleSwitch',
        'increment': 'increment',
        'decrement': 'decrement',
        'quick-bet': 'quickBet',
      };

      const soundKey = soundMap[soundName];
      if (soundKey && this.sounds[soundKey]) {
        this.sounds[soundKey].play();
      }
    } catch (error) {
      console.error(`Error playing sound ${soundName}:`, error);
      // Silent fallback
    }
  }

  /**
   * Start background music with optional fade-in
   * @param {boolean} loop - Whether to loop the music (default: true)
   * @param {number} fadeDuration - Fade-in duration in ms (default: 500)
   */
  playBackgroundMusic(loop = true, fadeDuration = 500) {
    if (!this.initialized || this.muted || !this.unlocked || !this.backgroundMusic) {
      return; // Don't play before audio is unlocked
    }

    try {
      // Stop if already playing
      if (this.backgroundMusic.playing()) {
        this.backgroundMusic.stop();
      }

      // Set loop property
      this.backgroundMusic.loop(loop);

      // Play with fade-in
      const soundId = this.backgroundMusic.play();
      this.backgroundMusic.fade(0, this.masterVolume * this.musicVolume, fadeDuration, soundId);
    } catch (error) {
      console.error('Error playing background music:', error);
    }
  }

  /**
   * Stop background music with optional fade-out
   * @param {number} fadeDuration - Fade-out duration in ms (default: 500)
   */
  stopBackgroundMusic(fadeDuration = 500) {
    if (!this.initialized || !this.backgroundMusic) {
      return;
    }

    try {
      if (this.backgroundMusic.playing()) {
        const soundId = this.backgroundMusic.play();
        this.backgroundMusic.fade(this.backgroundMusic.volume(), 0, fadeDuration, soundId);

        // Stop after fade completes
        setTimeout(() => {
          this.backgroundMusic.stop();
        }, fadeDuration);
      }
    } catch (error) {
      console.error('Error stopping background music:', error);
    }
  }

  /**
   * Set master volume (affects all sounds)
   * @param {number} volume - Volume level (0.0 to 1.0)
   */
  setMasterVolume(volume) {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    this.updateAllVolumes();
  }

  /**
   * Set SFX volume (affects sound effects only)
   * @param {number} volume - Volume level (0.0 to 1.0)
   */
  setSFXVolume(volume) {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
    this.updateAllVolumes();
  }

  /**
   * Set music volume (affects background music only)
   * @param {number} volume - Volume level (0.0 to 1.0)
   */
  setMusicVolume(volume) {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    this.updateAllVolumes();
  }

  /**
   * Update volumes for all loaded sounds
   */
  updateAllVolumes() {
    if (!this.initialized) {
      return;
    }

    try {
      // Update SFX volumes
      Object.values(this.sounds).forEach(sound => {
        if (sound) {
          sound.volume(this.masterVolume * this.sfxVolume);
        }
      });

      // Update music volume
      if (this.backgroundMusic) {
        this.backgroundMusic.volume(this.masterVolume * this.musicVolume);
      }
    } catch (error) {
      console.error('Error updating volumes:', error);
    }
  }

  /**
   * Toggle mute state
   * @param {boolean} mute - Whether to mute (if undefined, toggles current state)
   */
  toggleMute(mute) {
    if (mute !== undefined) {
      this.muted = mute;
    } else {
      this.muted = !this.muted;
    }

    if (!this.initialized) {
      return;
    }

    try {
      // Mute/unmute all sounds
      Object.values(this.sounds).forEach(sound => {
        if (sound) {
          sound.mute(this.muted);
        }
      });

      // Mute/unmute background music
      if (this.backgroundMusic) {
        this.backgroundMusic.mute(this.muted);
      }
    } catch (error) {
      console.error('Error toggling mute:', error);
    }
  }

  /**
   * Get current volume levels
   */
  getVolumes() {
    return {
      master: this.masterVolume,
      sfx: this.sfxVolume,
      music: this.musicVolume,
    };
  }

  /**
   * Get mute state
   */
  isMuted() {
    return this.muted;
  }

  /**
   * Cleanup - stop all sounds
   * Note: Don't reset initialized flag because of singleton pattern and React StrictMode
   */
  cleanup() {
    try {
      Object.values(this.sounds).forEach(sound => {
        if (sound) {
          sound.stop();
        }
      });

      if (this.backgroundMusic) {
        this.backgroundMusic.stop();
      }

      // Don't unload or reset initialized - singleton should persist
    } catch (error) {
      console.error('Error cleaning up SoundManager:', error);
    }
  }
}

// Export singleton instance
const soundManager = new SoundManager();
export default soundManager;
